import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import COLORS from '../../utils/Colors';

interface IBusinessNotFoundProps {
    reason: string;
    businessName?: string;
    onRetry?: () => void;
    isApiError?: boolean;
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    padding: 2rem;
    text-align: center;
    font-family: 'Inter', sans-serif;
`;

const ErrorIcon = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: #fee2e2;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    font-size: 2rem;
`;

const Title = styled.h1`
    font-size: 1.875rem;
    font-weight: 700;
    color: #111827;
    margin: 0 0 1rem 0;
`;

const Message = styled.p`
    font-size: 1.125rem;
    color: #6b7280;
    margin: 0 0 2rem 0;
    max-width: 32rem;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
`;

const PrimaryButton = styled(Link)`
    background-color: ${COLORS.mainTeal};
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s;

    &:hover {
        background-color: ${COLORS.mainTealDark || COLORS.mainTeal};
        transform: translateY(-1px);
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
`;

const SecondaryButton = styled(Link)`
    background-color: white;
    color: ${COLORS.mainTeal};
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    text-decoration: none;
    font-weight: 500;
    border: 2px solid ${COLORS.mainTeal};
    transition: all 0.2s;

    &:hover {
        background-color: #f9fafb;
        transform: translateY(-1px);
    }
`;

const RetryButton = styled.button`
    background-color: ${COLORS.mainTeal};
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background-color: ${COLORS.mainTealDark || COLORS.mainTeal};
        transform: translateY(-1px);
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
`;

const BusinessNotFound: React.FC<IBusinessNotFoundProps> = ({ reason, businessName, onRetry, isApiError }) => {
    return (
        <Container>
            <ErrorIcon>
                {isApiError ? '⚠️' : '🏪'}
            </ErrorIcon>
            <Title>
                {isApiError ? 'Error de conexión' : 'Negocio no encontrado'}
            </Title>
            <Message>
                {reason || 'Lo sentimos, no pudimos encontrar el negocio que buscas.'}
            </Message>
            <ButtonGroup>
                {isApiError && onRetry ? (
                    <RetryButton onClick={onRetry}>
                        Reintentar
                    </RetryButton>
                ) : (
                    <>
                        <PrimaryButton to="/">
                            Volver al inicio
                        </PrimaryButton>
                        <SecondaryButton to="/search">
                            Buscar negocios
                        </SecondaryButton>
                    </>
                )}
            </ButtonGroup>
        </Container>
    );
};

export default BusinessNotFound;
